/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import {translate} from '@ion/core/src/locale/locale';
import TertiaryButton from '@ion/dls/src/components/buttons/TertiaryButton';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import CanRequestCheckinConditionRow from './CanRequestCheckinConditionRow';
import {
  CHECKIN_CONDITION_TYPES,
  CheckinConditionCombinator,
  CheckinConditionDraftRow,
  CheckinConditionsDraft,
  CheckinConditionType,
  createDefaultCheckinConditionRow,
  getAvailableConditionTypes,
  getCheckinConditionTypeLabels,
  mapConditionTypesToLabels,
  VEHICLE_DISCOVERY_STATES,
  withCheckinConditionErrors
} from './CanRequestCheckinConditionUtils';
import {createRowId} from './CanRequestCreateRowUtils';

export interface CanRequestCheckinConditionsEditorProps {
  value: CheckinConditionsDraft;
  onChange: (next: CheckinConditionsDraft) => void;
  disabled?: boolean;
}

const useStyles = makeStyles(() => ({
  root : {
    display       : 'flex',
    flexDirection : 'column',
    marginBottom  : '16px',
    flexShrink    : 0
  },
  title : {
    ...dlsAlt('typography', 'label1'),
    color         : dlsColor('grey1'),
    letterSpacing : '1px',
    marginBottom  : '4px'
  },
  helperText : {
    ...dlsAlt('typography', 'body3'),
    color        : dlsColor('grey3'),
    marginBottom : '12px'
  }
}));

const getConditionValueLabels = (labels: Record<string, string>): Record<CheckinConditionType, string> => (
  mapConditionTypesToLabels(labels, {
    [CheckinConditionType.VehicleDiscovery] : 'vehicleDiscoveryValueLabel',
    [CheckinConditionType.Voltage]          : 'voltageValueLabel',
    [CheckinConditionType.GpsSpeed]         : 'gpsSpeedValueLabel'
  }) as Record<CheckinConditionType, string>
);

const updateRowById = (
  rows: CheckinConditionDraftRow[],
  rowId: string,
  update: (row: CheckinConditionDraftRow) => CheckinConditionDraftRow
): CheckinConditionDraftRow[] => (
  rows.map(function(row) {
    return row.rowId === rowId ? update(row) : row;
  })
);

/**
 * Renders the optional check-in condition builder for offline CAN requests,
 * letting the user compose Vehicle Discovery / Voltage / GPS Speed
 * thresholds the device evaluates before checking in to receive the request.
 * Each condition type can only be used by one row at a time, "Add Condition"
 * disappears once all supported types are in use, and each gap between rows
 * carries its own independent AND/OR combinator.
 *
 * ```typescript
 * <CanRequestCheckinConditionsEditor value={checkinConditions}
 *   onChange={onCheckinConditionsChange} />
 * ```
 *
 * @param   value    The check-in conditions draft (rows and their combinators).
 * @param   onChange Invoked with the next draft on any row or combinator change.
 * @param   disabled Disables every control when true.
 * @returns The check-in conditions editor component.
 */
const CanRequestCheckinConditionsEditor = ({
  value,
  onChange,
  disabled = false
}: CanRequestCheckinConditionsEditorProps) => {
  const classes             = useStyles();
  const {rows, combinators} = value;

  const labels = translate('vehicle-manager:vehicleHistoryGrid.canRequestCreateDialog.checkinConditions', {
    returnObjects : true
  }) || {};

  const {
    title = '',
    helperText = '',
    typeLabel = '',
    combinatorLabel = '',
    and = '',
    or = '',
    addCondition = '',
    removeCondition = '',
    vehicleDiscoveryStates = {}
  } = labels || {};

  const conditionTypeLabels  = useMemo(() => getCheckinConditionTypeLabels(labels), [labels]);
  const conditionValueLabels = useMemo(() => getConditionValueLabels(labels), [labels]);

  const combinatorOptions = useMemo(() => ([
    {label: and, value: CheckinConditionCombinator.And},
    {label: or, value: CheckinConditionCombinator.Or}
  ]), [and, or]);

  const vehicleDiscoveryStateOptions = useMemo(() => (
    VEHICLE_DISCOVERY_STATES.map(function(state) {
      return {
        label : `${state} - ${vehicleDiscoveryStates[state] || ''}`,
        value : `${state}`
      };
    })
  ), [vehicleDiscoveryStates]);

  const canAddCondition = rows.length < CHECKIN_CONDITION_TYPES.length;

  const onConditionTypeChange = useCallback((rowId: string, nextType: CheckinConditionType) => {
    const nextRows = updateRowById(rows, rowId, function(row) {
      return createDefaultCheckinConditionRow(row.rowId, nextType);
    });
    onChange({...value, rows: nextRows});
  }, [rows, value, onChange]);

  const onConditionValueChange = useCallback((rowId: string, nextValue: string) => {
    const nextRows = updateRowById(rows, rowId, function(row) {
      return withCheckinConditionErrors({...row, value: nextValue});
    });
    onChange({...value, rows: nextRows});
  }, [rows, value, onChange]);

  const onRemoveCondition = useCallback((rowId: string) => {
    const rowIndex = rows.findIndex(function(row) {
      return row.rowId === rowId;
    });
    const removeCombinatorIndex = Math.min(rowIndex, combinators.length - 1);
    const nextRows = rows.filter(function(row) {
      return row.rowId !== rowId;
    });
    const nextCombinators = combinators.filter(function(_combinator, index) {
      return index !== removeCombinatorIndex;
    });
    onChange({rows: nextRows, combinators: nextCombinators});
  }, [rows, combinators, onChange]);

  const onCombinatorChange = useCallback((gapIndex: number, nextCombinator: CheckinConditionCombinator) => {
    const nextCombinators = combinators.map(function(combinatorValue, index) {
      return index === gapIndex ? nextCombinator : combinatorValue;
    });
    onChange({...value, combinators: nextCombinators});
  }, [combinators, value, onChange]);

  function onAddCondition() {
    const [nextType] = getAvailableConditionTypes(rows);
    if (nextType !== undefined) {
      const nextRows        = [...rows, createDefaultCheckinConditionRow(createRowId(), nextType)];
      const nextCombinators = rows.length > 0 ? [...combinators, CheckinConditionCombinator.And] : combinators;
      onChange({rows: nextRows, combinators: nextCombinators});
    }
  }

  function getConditionTypeOptions(rowId: string) {
    return getAvailableConditionTypes(rows, rowId).map(function(type) {
      return {label: conditionTypeLabels[type], value: String(type)};
    });
  }

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        {title}
      </div>
      <div className={classes.helperText}>
        {helperText}
      </div>
      {rows.map(function(row, index) {
        return (
          <CanRequestCheckinConditionRow key={row.rowId}
            row={row}
            index={index}
            isLast={index === rows.length - 1}
            disabled={disabled}
            typeLabel={typeLabel}
            removeConditionLabel={removeCondition}
            combinatorLabel={combinatorLabel}
            typeOptions={getConditionTypeOptions(row.rowId)}
            conditionValueLabels={conditionValueLabels}
            vehicleDiscoveryStateOptions={vehicleDiscoveryStateOptions}
            combinatorOptions={combinatorOptions}
            combinatorValue={combinators[index]}
            onTypeChange={onConditionTypeChange}
            onValueChange={onConditionValueChange}
            onRemove={onRemoveCondition}
            onCombinatorChange={onCombinatorChange} />
        );
      })}
      <Render.If condition={canAddCondition}>
        <div>
          <TertiaryButton onClick={onAddCondition}
            disabled={disabled}
            startIcon={<Add />}>
            {addCondition}
          </TertiaryButton>
        </div>
      </Render.If>
    </div>
  );
};

export default CanRequestCheckinConditionsEditor;
